import { useState } from 'react';
import './AnnotationControls.css';

type AnnotationTab = 'rower' | 'environment' | 'tracking';

export type AnnotationOptionsState = {
  showSeatLabels: boolean;
  showWatts: boolean;
  showBladeArc: boolean;
  showPerformanceMetrics: boolean;
  showWind: boolean;
  showCurrent: boolean;
  showCompass: boolean;
};

type AnnotationControlsProps = {
  options: AnnotationOptionsState;
  onToggle: (key: keyof AnnotationOptionsState) => void;
};

/**
 * AnnotationControls - Tabbed interface for toggling visualization annotations
 * Organizes annotations by category: Rower Data, Environment, and Tracking
 */
export function AnnotationControls({ options, onToggle }: AnnotationControlsProps) {
  const [activeTab, setActiveTab] = useState<AnnotationTab>('rower');

  return (
    <div className="annotation-controls-tabbed">
      {/* Tab Navigation */}
      <div className="annotation-controls-tabbed__tabs">
        <button
          type="button"
          className={`annotation-controls-tabbed__tab ${
            activeTab === 'rower' ? 'annotation-controls-tabbed__tab--active' : ''
          }`}
          onClick={() => setActiveTab('rower')}
        >
          <svg
            className="annotation-controls-tabbed__tab-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          Rower Data
        </button>
        <button
          type="button"
          className={`annotation-controls-tabbed__tab ${
            activeTab === 'environment' ? 'annotation-controls-tabbed__tab--active' : ''
          }`}
          onClick={() => setActiveTab('environment')}
        >
          <svg
            className="annotation-controls-tabbed__tab-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
          </svg>
          Environment
        </button>
        <button
          type="button"
          className={`annotation-controls-tabbed__tab ${
            activeTab === 'tracking' ? 'annotation-controls-tabbed__tab--active' : ''
          }`}
          onClick={() => setActiveTab('tracking')}
        >
          <svg
            className="annotation-controls-tabbed__tab-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="3 11 22 2 13 21 11 13 3 11" />
          </svg>
          Tracking
        </button>
      </div>

      {/* Tab Content */}
      <div className="annotation-controls-tabbed__content">
        {activeTab === 'rower' && (
          <div className="annotation-controls-tabbed__buttons">
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showSeatLabels ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showSeatLabels')}
            >
              Seat Labels
            </button>
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showWatts ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showWatts')}
            >
              Watts
            </button>
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showBladeArc ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showBladeArc')}
            >
              Blade Arc
            </button>
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showPerformanceMetrics ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showPerformanceMetrics')}
            >
              Performance Metrics
            </button>
          </div>
        )}

        {activeTab === 'environment' && (
          <div className="annotation-controls-tabbed__buttons">
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showWind ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showWind')}
            >
              Wind
            </button>
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showCurrent ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showCurrent')}
            >
              Current
            </button>
          </div>
        )}

        {activeTab === 'tracking' && (
          <div className="annotation-controls-tabbed__buttons">
            <button
              type="button"
              className={`annotation-controls-tabbed__button ${
                options.showCompass ? 'annotation-controls-tabbed__button--active' : ''
              }`}
              onClick={() => onToggle('showCompass')}
            >
              Compass
            </button>
            <button
              type="button"
              className="annotation-controls-tabbed__button annotation-controls-tabbed__button--disabled"
              disabled
            >
              GPS Path (Coming Soon)
            </button>
            <button
              type="button"
              className="annotation-controls-tabbed__button annotation-controls-tabbed__button--disabled"
              disabled
            >
              Speed Over Ground (Coming Soon)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
